import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import LearningCenter from './pages/LearningCenter';
import Admin from './pages/Admin';
import LoadingScreen from './components/LoadingScreen';
import { ContactModalProvider } from './contexts/ContactModalContext';
import { AuthProvider } from './contexts/AuthContext';
import ScrollToTop from './components/ScrollToTop';
import Projects from './pages/Projects';
import Careers from './pages/Careers';
import Bio from './pages/Bio';
import { HelmetProvider } from 'react-helmet-async';

// Location Pages
import KannurLanding from './pages/locations/Kannur';
import IrittyLanding from './pages/locations/Iritty';
import TaliparambaLanding from './pages/locations/Taliparamba';

// Case Study Pages
import LuxuryVillaKannur from './pages/projects/LuxuryVillaKannur';
import ModernHomeIritty from './pages/projects/ModernHomeIritty';
import ContemporaryTaliparamba from './pages/projects/ContemporaryTaliparamba';

import WhatsAppButton from './components/WhatsAppButton';

// List of images to preload
const imagesToPreload = [
  './logo.png',
  './logo-fav.png',
  './about-image.jpg',
  './custom-home-construction-kannur.jpg',
  './house-renovation-iritty.jpg',
  './villa-builders-taliparamba.jpg'
];

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const location = useLocation();

  // Handle page transitions
  useEffect(() => {
    let mounted = true;

    const handleTransition = async () => {
      setIsTransitioning(true);
      
      // Force scroll to top
      window.scrollTo(0, 0);
      
      // Wait for DOM update
      await new Promise(resolve => requestAnimationFrame(resolve));
      
      if (mounted) {
        // Remove transition after content is ready
        setTimeout(() => {
          if (mounted) {
            setIsTransitioning(false);
          }
        }, 300);
      }
    };

    handleTransition();

    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  // Handle initial loading
  useEffect(() => {
    let mounted = true;

    const preloadImages = async () => {
      const imagePromises = imagesToPreload.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = () => resolve(true);
          img.onerror = () => resolve(false);
        });
      });

      try {
        await Promise.all(imagePromises);
      } catch (error) {
        console.error('Error preloading images:', error);
      }

      await new Promise(resolve => setTimeout(resolve, 1500));

      if (mounted) {
        setIsLoading(false);
      }
    };

    preloadImages();

    return () => {
      mounted = false;
    };
  }, []);

  const isBioPage = location.pathname === '/bio';

  return (
    <HelmetProvider>
      <AuthProvider>
        <ContactModalProvider>
        <ScrollToTop />
        {isLoading ? (
          <LoadingScreen />
        ) : (
          <>
          <div 
            className={`min-h-screen flex flex-col overflow-x-hidden ${
              isTransitioning 
                ? 'opacity-0 translate-y-4' 
                : 'opacity-100 translate-y-0'
            } transform transition-all duration-300 ease-out`}
          >
            {!isBioPage && <Navbar />}
            <main className="flex-grow relative">
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/services" element={<Services />} />
                <Route path="/learning-center" element={<LearningCenter />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/bio" element={<Bio />} />
                
                {/* Local Landing Pages */}
                <Route path="/locations/kannur" element={<KannurLanding />} />
                <Route path="/locations/iritty" element={<IrittyLanding />} />
                <Route path="/locations/taliparamba" element={<TaliparambaLanding />} />
                
                {/* Case Studies */}
                <Route path="/projects/luxury-villa-kannur" element={<LuxuryVillaKannur />} />
                <Route path="/projects/modern-home-iritty" element={<ModernHomeIritty />} />
                <Route path="/projects/contemporary-house-taliparamba" element={<ContemporaryTaliparamba />} />
              </Routes>
            </main>
            {!isBioPage && <Footer />}
          </div>
          {!isBioPage && <WhatsAppButton />}
          </>
        )}
        </ContactModalProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
